import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Download } from 'lucide-react';
import { Button } from '../ui/Button';

interface CallTrack {
  call_sid: string;
  date: string;
  duration_seconds: number;
  duration_formatted: string;
  audio_url: string | null;
}

interface PlaylistAudioPlayerProps {
  calls: CallTrack[];
  onCallChange?: (index: number) => void;
  className?: string;
}

const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

export const PlaylistAudioPlayer: React.FC<PlaylistAudioPlayerProps> = ({
  calls,
  onCallChange,
  className = ''
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentCall = calls[currentIndex];
  const totalDuration = calls.reduce((sum, call) => sum + call.duration_seconds, 0);
  
  // Calculate cumulative time for progress
  const getCumulativeTime = () => {
    let cumulative = 0;
    for (let i = 0; i < currentIndex; i++) {
      cumulative += calls[i].duration_seconds;
    }
    return cumulative + currentTime;
  };

  // Load audio when track changes
  useEffect(() => {
    if (audioRef.current && currentCall?.audio_url) {
      setIsLoading(true);
      setError(null);
      audioRef.current.src = currentCall.audio_url;
      audioRef.current.load();
    }
  }, [currentIndex, currentCall?.audio_url]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      if (isPlaying) {
        audio.play().catch(console.error);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      // Auto-play next track
      if (currentIndex < calls.length - 1) {
        setCurrentIndex(prev => prev + 1);
        onCallChange?.(currentIndex + 1);
      } else {
        setIsPlaying(false);
      }
    };

    const handleError = () => {
      setIsLoading(false);
      setError('Failed to load audio');
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [currentIndex, calls.length, isPlaying, onCallChange]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Update playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = () => {
    if (!audioRef.current || !currentCall?.audio_url) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  const skipPrevious = () => {
    if (currentTime > 3) {
      // If more than 3 seconds in, restart current track
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    } else if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      onCallChange?.(currentIndex - 1);
    }
  };

  const skipNext = () => {
    if (currentIndex < calls.length - 1) {
      setCurrentIndex(prev => prev + 1);
      onCallChange?.(currentIndex + 1);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    setIsMuted(vol === 0);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleDownload = () => {
    if (currentCall?.audio_url) {
      const link = document.createElement('a');
      link.href = currentCall.audio_url;
      link.download = `call_${currentCall.call_sid}.mp3`;
      link.click();
    }
  };

  if (!calls.length) {
    return (
      <div className={`bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center text-gray-500 ${className}`}>
        No recordings available
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-4 ${className}`}>
      {/* Hidden audio element */}
      <audio ref={audioRef} preload="metadata" />
      
      {/* Current track info */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm">
          <span className="text-indigo-600 dark:text-indigo-400 font-medium">
            Call {currentIndex + 1} of {calls.length}
          </span>
          <span className="text-gray-400 mx-2">•</span>
          <span className="text-gray-600 dark:text-gray-400">
            {formatDate(currentCall?.date || '')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Playback speed */}
          <select
            value={playbackRate}
            onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
            className="text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-300"
          >
            <option value={0.5}>0.5x</option>
            <option value={0.75}>0.75x</option>
            <option value={1}>1x</option>
            <option value={1.25}>1.25x</option>
            <option value={1.5}>1.5x</option>
            <option value={2}>2x</option>
          </select>
          
          {/* Download button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            disabled={!currentCall?.audio_url}
            className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            title="Download current call"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Progress bar - Total progress across all calls */}
      <div className="mb-3">
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-150"
            style={{ width: `${(getCumulativeTime() / totalDuration) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{formatTime(getCumulativeTime())}</span>
          <span>{formatTime(totalDuration)}</span>
        </div>
      </div>

      {/* Current track progress */}
      <div className="mb-4">
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={currentTime}
          onChange={handleSeek}
          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
          disabled={!currentCall?.audio_url || isLoading}
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Volume control */}
          <button
            onClick={toggleMute}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
          />
        </div>

        {/* Playback controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={skipPrevious}
            disabled={currentIndex === 0 && currentTime < 3}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <SkipBack className="w-6 h-6" />
          </button>
          
          <button
            onClick={togglePlay}
            disabled={!currentCall?.audio_url || isLoading}
            className="p-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-full shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-0.5" />
            )}
          </button>
          
          <button
            onClick={skipNext}
            disabled={currentIndex >= calls.length - 1}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <SkipForward className="w-6 h-6" />
          </button>
        </div>

        {/* Track indicator dots */}
        <div className="flex gap-1">
          {calls.slice(0, 10).map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setCurrentIndex(idx);
                onCallChange?.(idx);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'bg-indigo-500 scale-125' 
                  : idx < currentIndex 
                    ? 'bg-indigo-300 dark:bg-indigo-700' 
                    : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={`Call ${idx + 1}`}
            />
          ))}
          {calls.length > 10 && (
            <span className="text-xs text-gray-400 ml-1">+{calls.length - 10}</span>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-2 text-sm text-red-500 text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default PlaylistAudioPlayer;

